'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ProductIds, URL } from '@/config/constants';
import { cn } from '@/lib/utils';

export interface ClubQuizBannerProps {
  /** リンク先。未指定時はパーソナライズクイズページ */
  href?: string;
  /** クリック時コールバック（例: ドロワーを閉じる） */
  onClick?: () => void;
  /** Link に渡す class 名（余白などは呼び出し側で付与） */
  className?: string;
  /** 利用シーンに応じた見た目 */
  variant?: 'default' | 'compact';
}

/**
 * 髪質診断（15 QUESTIONS）への誘導バナー。
 * ヘッダーメニューやその他画面で再利用可能。ルートは Link。
 */
export function ClubQuizBanner({
  href = URL.quiz('sr', 'normal', ProductIds.SrSubs),
  onClick,
  className,
  variant = 'default',
}: ClubQuizBannerProps) {
  const isCompact = variant === 'compact';

  return (
    <Link
      href={href}
      className={cn(
        isCompact
          ? 'relative flex min-h-[80px] items-center gap-club-12 overflow-hidden rounded-[6px] bg-club-gray-700 px-club-16 py-club-16 text-white transition-opacity hover:opacity-90'
          : 'flex items-center gap-4 overflow-hidden rounded-lg bg-club-gray-600 px-4 py-4 text-white transition-opacity hover:opacity-90',
        className,
      )}
      onClick={onClick}
    >
      {isCompact ? (
        <>
          <div className="pointer-events-none absolute right-[-30px] top-[-44px] h-[120px] w-[120px] rounded-full bg-[linear-gradient(135deg,rgba(255,194,214,0.52)_0%,rgba(166,233,255,0.52)_100%)] blur-[36px]" />
          <div className="pointer-events-none absolute bottom-[-38px] right-[64px] h-[96px] w-[140px] rotate-[20deg] rounded-full bg-white/15 blur-[12px]" />
        </>
      ) : null}

      <div className="relative z-10 min-w-0 flex-1 text-left">
        <p
          className={cn(
            "font-['Neue_Haas_Unica_W1G'] font-normal not-italic uppercase text-white",
            isCompact
              ? 'text-left text-[13px] leading-[1.6] tracking-normal'
              : 'text-[17px] leading-[160%] tracking-[0.68px]',
          )}
        >
          15 QUESTIONS
        </p>
        <div
          className={cn(
            'mt-1 flex items-center',
            isCompact ? 'gap-club-8' : undefined,
          )}
        >
          <p
            className={cn(
              isCompact
                ? "text-[10px] leading-[1.5] tracking-[0.8px] text-club-gray-300 [font-feature-settings:'palt']"
                : 'text-club-p-xs text-white/90',
            )}
          >
            3分で、今のあなたの髪を知る
          </p>
          {isCompact ? (
            <Image
              src="/icon/club-icon/arrow-circ-primary.svg"
              alt=""
              width={16}
              height={16}
              className="shrink-0"
            />
          ) : null}
        </div>
      </div>
      <div
        className={cn(
          'relative shrink-0',
          isCompact ? 'z-10 h-[54px] w-[44px]' : 'h-14 w-20',
        )}
      >
        <Image
          src="/images/common/banner-bottle.png"
          alt=""
          fill
          className={cn(
            'object-contain object-right',
            isCompact ? 'scale-[1.04]' : undefined,
          )}
        />
      </div>
    </Link>
  );
}

export default ClubQuizBanner;
